import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import BackgroundIcons from '../../components/BackgroundIcons';
import MetricTooltip from '../../components/MetricTooltip';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip, Legend, Title
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, Title);

const EvaluationDashboard = () => {
  const [dateRange, setDateRange] = useState('90 days');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.feedback.getDashboard(dateRange);
      setDashboardData(response.data.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const dateRangeOptions = [
    { value: '7 days', label: '7 Days', icon: '📅' },
    { value: '30 days', label: '30 Days', icon: '📆' },
    { value: '90 days', label: '90 Days', icon: '🗓️' }
  ];

  const evalIcons = [
    { emoji: '📊', size: '4xl', opacity: '15' },
    { emoji: '🧪', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' },
    { emoji: '🎯', size: '4xl', opacity: '15' },
    { emoji: '💎', size: '4xl', opacity: '15' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 rounded-full shadow-2xl mb-6 animate-bounce">
            <span className="text-3xl animate-pulse">📊</span>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-serif italic">Loading evaluation dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-red-200/50 text-center">
          <span className="text-5xl mb-4 block">⚠️</span>
          <p className="text-red-600 font-semibold text-lg">{error}</p>
          <button onClick={fetchDashboardData} className="mt-4 px-6 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { quality, satisfaction, saveRate, performance, cost, timeSpent, vendorFunnel, ritualFunnel, sessions, pageTime } = dashboardData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={evalIcons} />

      <div className="container mx-auto px-4 py-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-12 relative">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">📊</span>
          </div>

          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Model Evaluation
          </h1>

          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">🧪</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>

          <p className="text-xl text-gray-700 font-light italic mb-8">
            AI performance insights & quality metrics ✨
          </p>

          {/* Date Range Pills */}
          <div className="flex items-center justify-center gap-3">
            {dateRangeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDateRange(opt.value)}
                className={`px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-lg ${
                  dateRange === opt.value
                    ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-purple-300 scale-105 animate-pulse'
                    : 'bg-white/80 backdrop-blur-lg text-gray-700 border-2 border-purple-200/50 hover:border-purple-400 hover:shadow-xl'
                }`}
              >
                <span className="mr-2">{opt.icon}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

      {/* Quality Metrics — Bar Chart + Doughnut + Table
          Bar chart: avg quality score per AI model (0-100)
          Doughnut: grade distribution (A-F) aggregated across all models
          Table: per-model breakdown with theme count, avg score, and grade counts
          Quality score = weighted: style consistency 30%, completeness 30%, originality 20%, actionability 15%, color harmony 5% */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-indigo-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">🎯</span>
          <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Quality Metrics by Model</span><MetricTooltip metricKey="section_quality" color="indigo" />
        </h2>
        {/* Charts Row */}
        {quality?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Avg Quality Score Bar Chart */}
            <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/50 rounded-2xl p-5 border border-indigo-200/30">
              <h3 className="text-sm font-bold text-indigo-500 uppercase tracking-wider mb-3">Avg Quality Score<MetricTooltip metricKey="quality_score" color="indigo" /></h3>
              <Bar
                data={{
                  labels: quality.map(r => r.ai_model?.charAt(0).toUpperCase() + r.ai_model?.slice(1)),
                  datasets: [{
                    label: 'Avg Quality Score',
                    data: quality.map(r => parseFloat(r.avg_quality_score) || 0),
                    backgroundColor: ['rgba(99,102,241,0.7)', 'rgba(168,85,247,0.7)', 'rgba(236,72,153,0.7)', 'rgba(59,130,246,0.7)'],
                    borderRadius: 8,
                    borderSkipped: false,
                  }]
                }}
                options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, max: 100, grid: { color: 'rgba(99,102,241,0.08)' } }, x: { grid: { display: false } } } }}
              />
            </div>
            {/* Grade Distribution Doughnut */}
            <div className="bg-gradient-to-br from-purple-50/50 to-pink-50/50 rounded-2xl p-5 border border-purple-200/30">
              <h3 className="text-sm font-bold text-purple-500 uppercase tracking-wider mb-3">Grade Distribution (All Models)<MetricTooltip metricKey="quality_grade" color="purple" /></h3>
              <div className="max-w-[250px] mx-auto">
                <Doughnut
                  data={{
                    labels: ['A', 'B', 'C', 'D', 'F'],
                    datasets: [{
                      data: [
                        quality.reduce((s, r) => s + Number(r.grade_a_count || 0), 0),
                        quality.reduce((s, r) => s + Number(r.grade_b_count || 0), 0),
                        quality.reduce((s, r) => s + Number(r.grade_c_count || 0), 0),
                        quality.reduce((s, r) => s + Number(r.grade_d_count || 0), 0),
                        quality.reduce((s, r) => s + Number(r.grade_f_count || 0), 0),
                      ],
                      backgroundColor: ['#22c55e', '#3b82f6', '#eab308', '#f97316', '#ef4444'],
                      borderWidth: 2, borderColor: '#fff',
                    }]
                  }}
                  options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true } }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed} themes` } } } }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">Model</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">Themes</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">Avg Score</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">A</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">B</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">C</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">D</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-600 uppercase tracking-wider">F</th>
              </tr>
            </thead>
            <tbody>
              {quality?.map((row) => (
                <tr key={row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)} className="border-b border-indigo-100/50 hover:bg-indigo-50/30 transition-colors duration-200">
                  <td className="px-4 py-3 font-serif font-bold text-gray-800">{row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)}</td>
                  <td className="px-4 py-3 text-gray-600">{row.total_themes}</td>
                  <td className="px-4 py-3">
                    <span className="px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md">
                      {row.avg_quality_score}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className="text-green-600 font-semibold">{row.grade_a_count}</span></td>
                  <td className="px-4 py-3"><span className="text-blue-600 font-semibold">{row.grade_b_count}</span></td>
                  <td className="px-4 py-3"><span className="text-yellow-600 font-semibold">{row.grade_c_count}</span></td>
                  <td className="px-4 py-3"><span className="text-orange-600 font-semibold">{row.grade_d_count}</span></td>
                  <td className="px-4 py-3"><span className="text-red-600 font-semibold">{row.grade_f_count}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>


      {/* Performance Metrics — Range Bar Cards
          One card per AI model showing response time as a visual range:
          - Green marker: fastest response (min)
          - Blue marker: average response (avg), positioned proportionally
          - Red marker: slowest response (max)
          - Gradient background bar shows the full min→max spectrum
          Replaces the previous bar chart + table for a more compact, visual layout */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-blue-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">⚡</span>
          <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Performance Metrics</span><MetricTooltip metricKey="section_performance" color="blue" />
        </h2>
        <div className="grid grid-cols-1 gap-4">
          {performance?.map((row) => {
            const min = parseFloat(row.min_response_time_ms) || 0;
            const avg = parseFloat(row.avg_response_time_ms) || 0;
            const max = parseFloat(row.max_response_time_ms) || 1;
            // Position avg marker proportionally between min and max
            const avgPct = ((avg - min) / (max - min || 1)) * 100;
            return (
              <div key={row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)} className="bg-gradient-to-br from-blue-50/60 to-cyan-50/60 rounded-2xl p-5 border border-blue-200/30 hover:shadow-lg transition-all duration-300">
                {/* Model name + request count */}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-serif font-bold text-gray-800 text-lg">{row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)}</span>
                  <span className="text-xs text-gray-400">{row.total_requests} requests<MetricTooltip metricKey="total_requests" color="blue" /></span>
                </div>
                {/* Range bar: green(min) → blue(avg) → red(max) markers on gradient track */}
                <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden mb-3">
                  <div className="absolute inset-y-0 rounded-full bg-gradient-to-r from-green-300 via-blue-400 to-red-300 opacity-30" style={{ left: '0%', right: '0%' }} />
                  <div className="absolute top-1 bottom-1 w-1 bg-green-500 rounded-full z-10" style={{ left: '2%' }} />
                  <div className="absolute top-0 bottom-0 w-1.5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full z-20 shadow-lg shadow-blue-300/50" style={{ left: `${Math.max(avgPct, 2)}%` }} />
                  <div className="absolute top-1 bottom-1 w-1 bg-red-500 rounded-full z-10" style={{ left: '97%' }} />
                </div>
                {/* Min / Avg / Max labels */}
                <div className="flex justify-between text-xs">
                  <span className="text-green-600 font-bold">⚡ {min}ms<MetricTooltip metricKey="min_response_time" color="green" /></span>
                  <span className="px-3 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold shadow-md">Avg {avg}ms<MetricTooltip metricKey="avg_response_time" color="blue" /></span>
                  <span className="text-red-500 font-bold">🔥 {max}ms<MetricTooltip metricKey="max_response_time" color="red" /></span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Cost Analysis — Bar Charts + Table
          Two charts: total cost by model (USD) and token usage (input vs output)
          Table: per-model breakdown with total cost, avg cost/request, and token counts
          Cost calculated from input + output token pricing per model */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-gold-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">💰</span>
          <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Cost Analysis</span><MetricTooltip metricKey="section_cost" color="amber" />
        </h2>
        {/* Cost + Token Charts */}
        {cost?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-yellow-50/50 to-orange-50/50 rounded-2xl p-5 border border-yellow-200/30">
              <h3 className="text-sm font-bold text-yellow-600 uppercase tracking-wider mb-3">Total Cost by Model (USD)<MetricTooltip metricKey="total_cost" color="yellow" /></h3>
              <Bar
                data={{
                  labels: cost.map(r => r.ai_model?.charAt(0).toUpperCase() + r.ai_model?.slice(1)),
                  datasets: [{
                    label: 'Total Cost ($)',
                    data: cost.map(r => parseFloat(r.total_cost_usd) || 0),
                    backgroundColor: ['rgba(234,179,8,0.7)', 'rgba(249,115,22,0.7)', 'rgba(239,68,68,0.7)', 'rgba(168,85,247,0.7)'],
                    borderRadius: 8, borderSkipped: false,
                  }]
                }}
                options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid: { color: 'rgba(234,179,8,0.08)' } }, x: { grid: { display: false } } } }}
              />
            </div>
            <div className="bg-gradient-to-br from-orange-50/50 to-red-50/50 rounded-2xl p-5 border border-orange-200/30">
              <h3 className="text-sm font-bold text-orange-600 uppercase tracking-wider mb-3">Token Usage<MetricTooltip metricKey="total_input_tokens" color="orange" /></h3>
              <Bar
                data={{
                  labels: cost.map(r => r.ai_model?.charAt(0).toUpperCase() + r.ai_model?.slice(1)),
                  datasets: [
                    { label: 'Input Tokens', data: cost.map(r => r.total_input_tokens || 0), backgroundColor: 'rgba(234,179,8,0.6)', borderRadius: 8, borderSkipped: false },
                    { label: 'Output Tokens', data: cost.map(r => r.total_output_tokens || 0), backgroundColor: 'rgba(249,115,22,0.7)', borderRadius: 8, borderSkipped: false },
                  ]
                }}
                options={{ responsive: true, plugins: { legend: { position: 'top', labels: { usePointStyle: true, padding: 15 } } }, scales: { y: { beginAtZero: true, grid: { color: 'rgba(249,115,22,0.08)' } }, x: { grid: { display: false } } } }}
              />
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-yellow-50 to-orange-50">
                <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Model</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Requests</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Total Cost</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Avg/Request</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Input Tokens</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Output Tokens</th>
              </tr>
            </thead>
            <tbody>
              {cost?.map((row) => (
                <tr key={row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)} className="border-b border-yellow-100/50 hover:bg-yellow-50/30 transition-colors duration-200">
                  <td className="px-4 py-3 font-serif font-bold text-gray-800">{row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)}</td>
                  <td className="px-4 py-3 text-gray-600">{row.total_requests}</td>
                  <td className="px-4 py-3">
                    <span className="px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md">
                      ${row.total_cost_usd}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-yellow-700">${row.avg_cost_per_request}</td>
                  <td className="px-4 py-3 text-gray-600">{row.total_input_tokens?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-600">{row.total_output_tokens?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Save Rate */}
      {/* Save Rate by Model — Radial Progress Rings
          One card per AI model with a CSS circular progress ring showing save rate %.
          - Ring fill: percentage of generated themes that were saved
          - Center: save rate % in bold
          - Below ring: generated and saved counts as small labels
          Replaces the previous bar chart + table for a compact, visual layout */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-emerald-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">📥</span>
          <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">Save Rate by Model</span><MetricTooltip metricKey="section_save_rate" color="emerald" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {saveRate?.map((row) => {
            const rate = parseFloat(row.save_rate_percentage) || 0;
            // SVG circle: radius=54, circumference=2πr≈339.3
            const circumference = 339.3;
            const offset = circumference - (rate / 100) * circumference;
            return (
              <div key={row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)} className="bg-gradient-to-br from-emerald-50/60 to-green-50/60 rounded-2xl p-6 border border-emerald-200/30 hover:shadow-lg transition-all duration-300 text-center">
                {/* Model name */}
                <p className="font-serif font-bold text-gray-800 text-lg mb-4">{row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)}</p>
                {/* Circular progress ring */}
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                    {/* Background track */}
                    <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                    {/* Progress arc — offset controls fill amount */}
                    <circle cx="60" cy="60" r="54" fill="none" stroke="url(#emeraldGrad)" strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={circumference} strokeDashoffset={offset}
                      className="transition-all duration-1000" />
                    <defs>
                      <linearGradient id="emeraldGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#22c55e" />
                      </linearGradient>
                    </defs>
                  </svg>
                  {/* Center percentage label */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-500 bg-clip-text text-transparent">{rate}%</span>
                  </div>
                </div>
                {/* Generated / Saved counts */}
                <div className="flex justify-center gap-4 text-xs">
                  <span className="text-gray-500">{row.generated_count} generated<MetricTooltip metricKey="generated_count" color="emerald" /></span>
                  <span className="text-emerald-600 font-bold">{row.saved_count} saved<MetricTooltip metricKey="saved_count" color="emerald" /></span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* User Satisfaction — Sentiment Gauge Cards
          One card per AI model showing:
          - Star rating displayed as filled/empty stars (1-5 scale)
          - Horizontal sentiment bar: green (thumbs up %) ← → red (thumbs down %)
          - Thumbs up/down counts as labels on each end
          - Total ratings count
          Replaces the previous bar chart + table for a compact, emotional layout */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-pink-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">⭐</span>
          <span className="bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">User Satisfaction by Model</span><MetricTooltip metricKey="section_satisfaction" color="rose" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {satisfaction?.map((row) => {
            const upPct = parseFloat(row.thumbs_up_percentage) || 0;
            const rating = parseFloat(row.avg_rating) || 0;
            return (
              <div key={row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)} className="bg-gradient-to-br from-pink-50/60 to-rose-50/60 rounded-2xl p-6 border border-pink-200/30 hover:shadow-lg transition-all duration-300">
                {/* Model name + total ratings */}
                <div className="flex items-center justify-between mb-4">
                  <span className="font-serif font-bold text-gray-800 text-lg">{row.ai_model?.charAt(0).toUpperCase() + row.ai_model?.slice(1)}</span>
                  <span className="text-xs text-gray-400">{row.total_ratings} ratings<MetricTooltip metricKey="star_rating" color="pink" /></span>
                </div>
                {/* Star rating — filled stars based on avg rating (1-5) */}
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={`text-2xl ${star <= Math.round(rating) ? 'opacity-100' : 'opacity-20'}`}>⭐</span>
                  ))}
                  <span className="ml-2 text-lg font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent">{rating || 'N/A'}</span>
                </div>
                {/* Sentiment bar — green(up) vs red(down) proportional fill */}
                <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden mb-2">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-l-full transition-all duration-1000" style={{ width: `${upPct}%` }} />
                  <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-red-400 to-rose-400 rounded-r-full transition-all duration-1000" style={{ width: `${100 - upPct}%` }} />
                </div>
                {/* Thumbs counts on each end */}
                <div className="flex justify-between text-xs">
                  <span className="text-green-600 font-bold">👍 {row.thumbs_up}<MetricTooltip metricKey="thumbs_up" color="green" /></span>
                  <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold text-[10px]">{upPct}% positive<MetricTooltip metricKey="thumbs_up_pct" color="pink" inverted /></span>
                  <span className="text-red-500 font-bold">👎 {row.thumbs_down}<MetricTooltip metricKey="thumbs_down" color="red" /></span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Vendor Conversion Funnel — Horizontal Flow + Pipeline Bars + All Visitor Mini-Card
          Top: horizontal connected flow showing Navigation → rate → Conversion → rate → Contacts
          Middle: pipeline bars Views → Shortlists → Contacts with proportional widths
          Bottom: "All Visitor Interactions" mini-card with unfiltered totals
          All funnel counts filtered by entry_point != 'direct' */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-rose-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">🏪</span>
          <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">Vendor Conversion Funnel</span><MetricTooltip metricKey="section_vendor_funnel" color="rose" />
        </h2>

        {/* Horizontal funnel flow: Navigation → Conv Rate → Conversion → Contact Rate → Pipeline */}
        <div className="flex items-center justify-center gap-0 mb-8 flex-wrap md:flex-nowrap">
          {/* Navigation step */}
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl px-6 py-5 text-center border border-rose-200/50 min-w-[140px] hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl block mb-1">🔍</span>
            <p className="text-3xl font-bold text-gray-800">{vendorFunnel?.navigation_count || 0}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">Navigation<MetricTooltip metricKey="vendor_navigation" color="rose" /></p>
          </div>
          {/* Arrow + Nav → Conv Rate */}
          <div className="flex items-center px-1">
            <div className="h-0.5 w-4 bg-gradient-to-r from-rose-300 to-pink-400" />
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-md whitespace-nowrap">
              {vendorFunnel?.navigation_to_conversion_rate || 0}%<MetricTooltip metricKey="vendor_nav_conv_rate" color="rose" inverted />
            </span>
            <div className="h-0.5 w-4 bg-gradient-to-r from-pink-400 to-purple-300" />
          </div>
          {/* Conversion step */}
          <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl px-6 py-5 text-center border border-pink-200/50 min-w-[140px] hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl block mb-1">✅</span>
            <p className="text-3xl font-bold text-gray-800">{vendorFunnel?.conversion_count || 0}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">Conversion<MetricTooltip metricKey="vendor_conversion" color="pink" /></p>
          </div>
          {/* Arrow + Conv → Contact Rate */}
          <div className="flex items-center px-1">
            <div className="h-0.5 w-4 bg-gradient-to-r from-purple-300 to-indigo-400" />
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-md whitespace-nowrap">
              {vendorFunnel?.conversion_to_contact_rate || 0}%<MetricTooltip metricKey="vendor_conv_contact_rate" color="purple" inverted />
            </span>
            <div className="h-0.5 w-4 bg-gradient-to-r from-indigo-400 to-purple-300" />
          </div>
          {/* Contact step */}
          <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl px-6 py-5 text-center border border-purple-200/50 min-w-[140px] hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl block mb-1">📞</span>
            <p className="text-3xl font-bold text-gray-800">{vendorFunnel?.contact_count || 0}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">Contacts<MetricTooltip metricKey="vendor_contacts" color="purple" /></p>
          </div>
        </div>

        {/* Pipeline breakdown: Views → Shortlists → Contacts with proportional widths */}
        <div className="space-y-3 mb-6">
          {[
            { stage: 'Views', count: vendorFunnel?.view_count || 0, width: '100%', color: 'from-rose-400 to-rose-500', tip: 'vendor_views' },
            { stage: 'Shortlists', count: vendorFunnel?.shortlist_count || 0, width: `${Math.max(((vendorFunnel?.shortlist_count || 0) / Math.max(vendorFunnel?.view_count || 1, 1)) * 100, 15)}%`, color: 'from-pink-400 to-pink-500', tip: 'vendor_shortlists' },
            { stage: 'Contacts', count: vendorFunnel?.contact_count || 0, width: `${Math.max(((vendorFunnel?.contact_count || 0) / Math.max(vendorFunnel?.view_count || 1, 1)) * 100, 10)}%`, color: 'from-purple-400 to-purple-500', tip: 'vendor_contacts' },
          ].map((step, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <span className="w-28 text-sm font-bold text-gray-700 text-right flex items-center justify-end">{step.stage}<MetricTooltip metricKey={step.tip} color="rose" /></span>
              <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                <div className={`bg-gradient-to-r ${step.color} h-full rounded-full flex items-center justify-end pr-3 transition-all duration-1000`} style={{ width: step.width }}>
                  <span className="text-white text-sm font-bold">{step.count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* All Visitor Interactions (incl. direct) */}
        <div className="bg-gradient-to-r from-rose-50/80 via-pink-50/80 to-purple-50/80 backdrop-blur-sm rounded-2xl p-4 border border-rose-200/30">
          <p className="text-xs font-semibold text-gray-500 mb-3 tracking-wide">📊 All Visitor Interactions <span className="text-gray-400">(incl. direct)</span></p>
          <div className="flex gap-4 justify-around">
            {[
              { label: 'Views', value: vendorFunnel?.total_view_count || 0, icon: '👁️', tip: 'vendor_total_views' },
              { label: 'Shortlists', value: vendorFunnel?.total_shortlist_count || 0, icon: '⭐', tip: 'vendor_total_shortlists' },
              { label: 'Contacts', value: vendorFunnel?.total_contact_count || 0, icon: '📞', tip: 'vendor_total_contacts' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-lg">{s.icon}</span>
                <p className="text-xl font-bold text-gray-700">{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}<MetricTooltip metricKey={s.tip} color="rose" /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ritual Conversion Funnel — Horizontal Flow + Entry Points + Saves + All Visitor Mini-Card
          Top: horizontal connected flow showing Navigation → rate → Conversion → Saves
          Entry point split (theme page vs modal) shown as pills inside Navigation step
          Bottom: "All Visitor Interactions" mini-card with unfiltered totals
          All funnel counts filtered by entry_point != 'direct' */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-orange-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">🎭</span>
          <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Ritual Conversion Funnel</span><MetricTooltip metricKey="section_ritual_funnel" color="orange" />
        </h2>

        {/* Horizontal flow: Navigation → Conv Rate → Conversion → Saves */}
        <div className="flex items-center justify-center gap-0 mb-8 flex-wrap md:flex-nowrap">
          {/* Navigation step with entry point breakdown */}
          <div className="bg-gradient-to-br from-orange-100 to-yellow-100 rounded-2xl px-6 py-5 text-center border border-orange-200/50 min-w-[160px] hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl block mb-1">🔍</span>
            <p className="text-3xl font-bold text-gray-800">{ritualFunnel?.navigation_count || 0}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">Navigation<MetricTooltip metricKey="ritual_navigation" color="orange" /></p>
            {/* Entry point split */}
            <div className="flex justify-center gap-2 mt-2 text-[10px]">
              <span className="px-2 py-0.5 rounded-full bg-orange-200/60 text-orange-700 font-bold">🎨 Theme {ritualFunnel?.theme_entry_count || 0}<MetricTooltip metricKey="ritual_theme_entry" color="orange" /></span>
              <span className="px-2 py-0.5 rounded-full bg-yellow-200/60 text-yellow-700 font-bold">💬 Modal {ritualFunnel?.modal_entry_count || 0}<MetricTooltip metricKey="ritual_modal_entry" color="amber" /></span>
            </div>
          </div>
          {/* Arrow + Nav → Conv Rate */}
          <div className="flex items-center px-1">
            <div className="h-0.5 w-4 bg-gradient-to-r from-orange-300 to-red-400" />
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md whitespace-nowrap">
              {ritualFunnel?.navigation_to_conversion_rate || 0}%<MetricTooltip metricKey="ritual_nav_conv_rate" color="orange" inverted />
            </span>
            <div className="h-0.5 w-4 bg-gradient-to-r from-red-400 to-red-300" />
          </div>
          {/* Conversion step */}
          <div className="bg-gradient-to-br from-red-100 to-rose-100 rounded-2xl px-6 py-5 text-center border border-red-200/50 min-w-[140px] hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl block mb-1">✅</span>
            <p className="text-3xl font-bold text-gray-800">{ritualFunnel?.conversion_count || 0}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">Conversion<MetricTooltip metricKey="ritual_conversion" color="red" /></p>
          </div>
          {/* Arrow to saves */}
          <div className="flex items-center px-1">
            <div className="h-0.5 w-8 bg-gradient-to-r from-red-300 to-rose-300" />
          </div>
          {/* Saves step */}
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl px-6 py-5 text-center border border-rose-200/50 min-w-[140px] hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl block mb-1">📥</span>
            <p className="text-3xl font-bold text-gray-800">{ritualFunnel?.save_count || 0}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">Saves<MetricTooltip metricKey="ritual_saves" color="rose" /></p>
          </div>
        </div>

        {/* All Visitor Interactions (incl. direct) */}
        <div className="bg-gradient-to-r from-orange-50/80 via-red-50/80 to-rose-50/80 backdrop-blur-sm rounded-2xl p-4 border border-orange-200/30">
          <p className="text-xs font-semibold text-gray-500 mb-3 tracking-wide">📊 All Visitor Interactions <span className="text-gray-400">(incl. direct)</span></p>
          <div className="flex gap-4 justify-around">
            {[
              { label: 'Visitors', value: ritualFunnel?.total_visitor_count || 0, icon: '👁️', tip: 'ritual_total_visitors' },
              { label: 'Saves', value: ritualFunnel?.total_save_count || 0, icon: '📥', tip: 'ritual_total_saves' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-lg">{s.icon}</span>
                <p className="text-xl font-bold text-gray-700">{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}<MetricTooltip metricKey={s.tip} color="orange" /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Session Overview — Stat Cards Grid
          6 cards: total sessions, avg/min/max duration, logged-in vs anonymous counts
          Sessions tracked via heartbeat in sessionTracker.js
          Duration measured from first to last page interaction */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-purple-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">👥</span>
          <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">Session Overview</span><MetricTooltip metricKey="section_sessions" color="purple" />
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Total Sessions', value: sessions?.total_sessions || 0, icon: '📊', color: 'purple', tip: 'total_sessions' },
            { label: 'Avg Duration', value: `${sessions?.avg_duration_seconds || 0}s`, icon: '⏱️', color: 'blue', tip: 'avg_session_time' },
            { label: 'Min Duration', value: `${sessions?.min_duration_seconds || 0}s`, icon: '⚡', color: 'green', tip: 'min_session_time' },
            { label: 'Max Duration', value: `${sessions?.max_duration_seconds || 0}s`, icon: '🕐', color: 'orange', tip: 'max_session_time' },
            { label: 'Logged-in', value: sessions?.logged_in_sessions || 0, icon: '🔐', color: 'indigo', tip: 'logged_in_sessions' },
            { label: 'Anonymous', value: sessions?.anonymous_sessions || 0, icon: '👤', color: 'gray', tip: 'anonymous_sessions' },
          ].map((stat, idx) => (
            <div key={idx} className={`bg-gradient-to-br from-${stat.color}-50 to-${stat.color}-100/50 rounded-2xl p-4 text-center border border-${stat.color}-200/50 hover:scale-105 transition-transform duration-300`}>
              <span className="text-2xl block mb-2">{stat.icon}</span>
              <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              <p className="text-xs text-gray-500 font-medium mt-1">{stat.label}<MetricTooltip metricKey={stat.tip} color="purple" /></p>
            </div>
          ))}
        </div>
      </section>

      {/* Per-Page Time Breakdown — Horizontal Bar Chart + Table
          Chart: avg time per page sorted descending (horizontal bars)
          Table: page name, visit count, avg/max/total time in seconds
          Path names mapped to friendly labels via pageNames object
          Trailing slashes normalized in SQL (RTRIM) and sessionTracker */}
      <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-teal-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
        <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
          <span className="text-3xl mr-3">⏱️</span>
          <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Per-Page Time Breakdown</span><MetricTooltip metricKey="section_page_time" color="teal" />
        </h2>
        {/* Page Time Horizontal Bar */}
        {pageTime?.length > 0 && (() => {
          const pageNames = {
            '/': 'Home', '/dashboard': 'Dashboard Home', '/dashboard/': 'Dashboard Home',
            '/dashboard/vendors': 'Vendor Management', '/dashboard/vendors/': 'Vendor Management',
            '/dashboard/themes': 'Themes Dashboard', '/dashboard/themes/': 'Themes Dashboard',
            '/dashboard/budget': 'Budget Management', '/dashboard/budget/': 'Budget Management',
            '/dashboard/invitations': 'Invitations Dashboard', '/dashboard/invitations/': 'Invitations Dashboard',
            '/dashboard/rituals': 'Saved Rituals Dashboard', '/dashboard/rituals/': 'Saved Rituals Dashboard',
            '/dashboard/timeline': 'Timeline Dashboard', '/dashboard/timeline/': 'Timeline Dashboard',
            '/profile': 'Profile', '/profile/': 'Profile',
            '/wedding-blueprint/dream-wedding-generator': 'Dream Wedding Generator', '/wedding-blueprint/dream-wedding-generator/': 'Dream Wedding Generator',
            '/wedding-blueprint/moodboard-matcher': 'Moodboard Matcher', '/wedding-blueprint/moodboard-matcher/': 'Moodboard Matcher',
            '/wedding-blueprint/budget-allocator': 'Budget Allocator', '/wedding-blueprint/budget-allocator/': 'Budget Allocator',
            '/vendor-vault/smart-vendor-matching': 'Smart Vendor Matching', '/vendor-vault/smart-vendor-matching/': 'Smart Vendor Matching',
            '/vendor-vault/vendor-shortlisting-assistant': 'Vendor Shortlisting', '/vendor-vault/vendor-shortlisting-assistant/': 'Vendor Shortlisting',
            '/wedcraft-studio/invite-generator': 'Invite Generator', '/wedcraft-studio/invite-generator/': 'Invite Generator',
            '/wedcraft-studio/cultural-ritual-explainer': 'Cultural Ritual Explainer', '/wedcraft-studio/cultural-ritual-explainer/': 'Cultural Ritual Explainer',
            '/big-day-playbook/timeline-generator': 'Timeline Generator', '/big-day-playbook/timeline-generator/': 'Timeline Generator',
            '/big-day-playbook/weather-risk-advisor': 'Weather Risk Advisor', '/big-day-playbook/weather-risk-advisor/': 'Weather Risk Advisor',
            '/ever-after-memories/thank-you-note-generator': 'Thank You Notes', '/ever-after-memories/thank-you-note-generator/': 'Thank You Notes',
            '/ever-after-memories/memory-capsule-creator': 'Memory Capsule Creator', '/ever-after-memories/memory-capsule-creator/': 'Memory Capsule Creator',
            '/admin/evaluation': 'Model Evaluation', '/admin/evaluation/': 'Model Evaluation',
            '/admin/ab-testing': 'A/B Testing', '/admin/ab-testing/': 'A/B Testing',
            '/admin/vowai-labs': 'VowAI Labs', '/admin/vowai-labs/': 'VowAI Labs',
            '/vowai-labs': 'VowAI Labs', '/vowai-labs/': 'VowAI Labs',
            '/reset-password': 'Reset Password', '/reset-password/': 'Reset Password',
          };
          const sorted = [...pageTime].sort((a, b) => parseFloat(b.avg_time_seconds) - parseFloat(a.avg_time_seconds));
          return (
            <div className="bg-gradient-to-br from-teal-50/50 to-cyan-50/50 rounded-2xl p-5 border border-teal-200/30 mb-8">
              <h3 className="text-sm font-bold text-teal-500 uppercase tracking-wider mb-3">Avg Time per Page (seconds) — sorted<MetricTooltip metricKey="avg_time_seconds" color="teal" /></h3>
              <Bar
                data={{
                  labels: sorted.map(r => pageNames[r.page] || r.page),
                  datasets: [{
                    label: 'Avg Time (s)',
                    data: sorted.map(r => parseFloat(r.avg_time_seconds) || 0),
                    backgroundColor: 'rgba(20,184,166,0.6)',
                    borderRadius: 8, borderSkipped: false,
                  }]
                }}
                options={{
                  indexAxis: 'y', responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { x: { beginAtZero: true, grid: { color: 'rgba(20,184,166,0.08)' } }, y: { grid: { display: false } } }
                }}
              />
            </div>
          );
        })()}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-teal-50 to-cyan-50">
                <th className="px-4 py-3 text-left text-sm font-bold text-teal-600 uppercase tracking-wider">Page</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-teal-600 uppercase tracking-wider">Visits</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-teal-600 uppercase tracking-wider">Avg Time (s)</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-teal-600 uppercase tracking-wider">Max Time (s)</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-teal-600 uppercase tracking-wider">Total Time (s)</th>
              </tr>
            </thead>
            <tbody>
              {pageTime?.map((row, idx) => {
                const pageNames = {
                  '/': 'Home', '/dashboard': 'Dashboard Home', '/dashboard/': 'Dashboard Home',
                  '/dashboard/vendors': 'Vendor Management', '/dashboard/vendors/': 'Vendor Management',
                  '/dashboard/themes': 'Themes Dashboard', '/dashboard/themes/': 'Themes Dashboard',
                  '/dashboard/budget': 'Budget Management', '/dashboard/budget/': 'Budget Management',
                  '/dashboard/invitations': 'Invitations Dashboard', '/dashboard/invitations/': 'Invitations Dashboard',
                  '/dashboard/rituals': 'Saved Rituals Dashboard', '/dashboard/rituals/': 'Saved Rituals Dashboard',
                  '/dashboard/timeline': 'Timeline Dashboard', '/dashboard/timeline/': 'Timeline Dashboard',
                  '/profile': 'Profile', '/profile/': 'Profile',
                  '/wedding-blueprint/dream-wedding-generator': 'Dream Wedding Generator', '/wedding-blueprint/dream-wedding-generator/': 'Dream Wedding Generator',
                  '/wedding-blueprint/moodboard-matcher': 'Moodboard Matcher', '/wedding-blueprint/moodboard-matcher/': 'Moodboard Matcher',
                  '/wedding-blueprint/budget-allocator': 'Budget Allocator', '/wedding-blueprint/budget-allocator/': 'Budget Allocator',
                  '/vendor-vault/smart-vendor-matching': 'Smart Vendor Matching', '/vendor-vault/smart-vendor-matching/': 'Smart Vendor Matching',
                  '/vendor-vault/vendor-shortlisting-assistant': 'Vendor Shortlisting', '/vendor-vault/vendor-shortlisting-assistant/': 'Vendor Shortlisting',
                  '/wedcraft-studio/invite-generator': 'Invite Generator', '/wedcraft-studio/invite-generator/': 'Invite Generator',
                  '/wedcraft-studio/cultural-ritual-explainer': 'Cultural Ritual Explainer', '/wedcraft-studio/cultural-ritual-explainer/': 'Cultural Ritual Explainer',
                  '/big-day-playbook/timeline-generator': 'Timeline Generator', '/big-day-playbook/timeline-generator/': 'Timeline Generator',
                  '/big-day-playbook/weather-risk-advisor': 'Weather Risk Advisor', '/big-day-playbook/weather-risk-advisor/': 'Weather Risk Advisor',
                  '/ever-after-memories/thank-you-note-generator': 'Thank You Notes', '/ever-after-memories/thank-you-note-generator/': 'Thank You Notes',
                  '/ever-after-memories/memory-capsule-creator': 'Memory Capsule Creator', '/ever-after-memories/memory-capsule-creator/': 'Memory Capsule Creator',
                  '/admin/evaluation': 'Model Evaluation', '/admin/evaluation/': 'Model Evaluation',
                  '/admin/ab-testing': 'A/B Testing', '/admin/ab-testing/': 'A/B Testing',
                  '/admin/vowai-labs': 'VowAI Labs', '/admin/vowai-labs/': 'VowAI Labs',
                  '/vowai-labs': 'VowAI Labs', '/vowai-labs/': 'VowAI Labs',
                  '/reset-password': 'Reset Password', '/reset-password/': 'Reset Password',
                };
                const displayName = pageNames[row.page] || row.page;
                return (
                  <tr key={idx} className="border-b border-teal-100/50 hover:bg-teal-50/30 transition-colors duration-200">
                    <td className="px-4 py-3 font-serif font-bold text-gray-800">{displayName}</td>
                    <td className="px-4 py-3 text-gray-600">{row.total_visits}</td>
                    <td className="px-4 py-3">
                      <span className="px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-md">
                        {row.avg_time_seconds}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.max_time_seconds}</td>
                    <td className="px-4 py-3 text-gray-600">{row.total_time_seconds}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>


      </div>
    </div>
  );
};

export default EvaluationDashboard;
